import {
  DefaultMantineColor,
  BadgeProps,
  useCombobox,
  Badge,
  CheckIcon,
  Combobox as Combo,
  Tooltip,
  CloseButton,
  PillsInput,
  Pill,
  PillsInputProps,
  ComboboxStore,
  Button
} from '@mantine/core'
import {useDebouncedValue} from '@mantine/hooks'
import {useStickyOrderRequired} from 'components/hooks/useStickyOrder'
import {ReactNode, useState} from 'react'
import {isEmpty, pipe, sortBy} from 'remeda'
import {entries} from 'shared/collection'
import {escapeRegExp} from 'shared/text'
import {WrapIf} from 'shared/ui'

export type ItemMd = {
  color?: DefaultMantineColor
  label?: string
  desc?: string
  count?: number
}
export type ItemProps<T extends string = string, M extends ItemMd = ItemMd> = {
  value: T
  selected: boolean
  visible: boolean
  id: string
} & ItemMd &
  M
export type ComboMultiProps<T extends string = string, M extends ItemMd = ItemMd> = {
  md: Record<T, M>
  value?: T[]
  onChange: (values: T[], delta: {mode: 'add' | 'remove'; value: T} | {mode: 'clear'}) => void
  /* the target of the dropdown. BYO everything including opening the combo on a button press */
  children?: (values: ItemProps<T, M>[], combo: ComboboxStore) => ReactNode
  /** a custom element inside the default target button */
  target?: (values: ItemProps<T, M>[]) => ReactNode
  item?: (value: ItemProps<T, M>) => ReactNode
  emptyTarget?: ReactNode
  buttonProps?: React.ComponentPropsWithoutRef<typeof Button>
  badgeProps?: (value?: ItemProps<T, M>) => Partial<BadgeProps>
  /** a plural naming the thing you are listing. e.g. "forecasts" */
  itemsLabel?: string
  emptyLabel?: string
  hideZeros?: boolean
  showClear?: boolean
  showSearch?: boolean
  searchPlaceholder?: string
  disableSort?: boolean
  onDropDownClose?: () => void
  onDropDownOpen?: () => void
  classNames?: {target?: string; input?: string}
  withinPortal?: boolean
  disabled?: boolean
}

export const ComboMulti = <T extends string, M extends ItemMd = ItemMd>({
  value: values, // we use value to be compatible with form.getInputProps
  onChange,
  md,
  children,
  target,
  item,
  emptyTarget,
  buttonProps,
  badgeProps,
  itemsLabel,
  emptyLabel,
  hideZeros,
  showClear,
  showSearch,
  searchPlaceholder,
  classNames,
  onDropDownClose,
  onDropDownOpen,
  disableSort,
  withinPortal,
  disabled
}: ComboMultiProps<T, M>) => {
  const [search, setSearch] = useState('')
  const [searchDebounced] = useDebouncedValue(search, 200)
  const combo = useCombobox({
    onDropdownClose() {
      if (showSearch) setSearch('')
      if (!disableSort) resetOrder()
      onDropDownClose?.()
    },
    onDropdownOpen() {
      if (showSearch) combo.focusSearchInput()
      onDropDownOpen?.()
    }
  })
  const {data: items, reset: resetOrder} = useStickyOrderRequired(
    pipe(
      entries(md).map(([value, md]) => {
        const selected = values?.includes(value)
        return {
          id: value,
          value,
          ...md,
          selected,
          label: md.label ?? value,
          visible:
            (!hideZeros || (md.count != null && md.count > 0) || selected) &&
            (!showSearch ||
              isEmpty(searchDebounced) ||
              md.label == null ||
              md.label.match(new RegExp(escapeRegExp(searchDebounced), 'i')))
        } as ItemProps<T, M>
      }),
      mds =>
        disableSort ? mds : (
          sortBy(
            mds,
            r => (r.selected ? 0 : 1),
            [r => r.count ?? 0, 'desc'],
            r => r.label?.toLowerCase() ?? ''
          )
        )
    ),
    {disabled: disableSort}
  )
  const selectedItems = items.filter(i => i.selected)

  const TargetRight = ({hideChev, hideClear, color}: {hideChev?: boolean; hideClear?: boolean; color?: string}) => (
    <>
      {!hideChev && <Combo.Chevron style={{color: color}} className={`ml-1`} />}
      {!hideClear && showClear && selectedItems.length > 0 && (
        <CloseButton
          className='point'
          size='sm'
          color={color}
          onClick={e => {
            e.stopPropagation()
            onChange([], {mode: 'clear'})
          }}
        />
      )}
    </>
  )

  return (
    <Combo
      store={combo}
      position='bottom-start'
      withinPortal={withinPortal}
      disabled={disabled}
      onOptionSubmit={v => {
        const value = v as T
        if (v == null || values == null) return
        const mode = values.includes(value) ? 'remove' : 'add'
        const newValues = mode == 'remove' ? values.filter(v => v != value) : [...values, value]
        onChange(newValues, {mode, value})
      }}>
      <Combo.Target>
        <div className={`${classNames?.target ?? ''} flex items-center`}>
          {children ?
            children(items, combo)
          : <>
              <Button
                component='div'
                className={`${classNames?.input ?? ''}`}
                size='compact-md'
                onClick={() => combo.toggleDropdown()}
                {...buttonProps}>
                {isEmpty(selectedItems) ?
                  emptyTarget ?
                    <>
                      {emptyTarget}
                      <TargetRight />
                    </>
                  : target ?
                    <>
                      {target(items)}
                      <TargetRight />
                    </>
                    //  we have TargetRight after target in all branches except for this case where it looks nice inside the badge 🥲
                  : <Badge color='gray.4' {...badgeProps?.()} rightSection={<TargetRight color='var(--badge-color)' />}>
                      {emptyLabel ?? (itemsLabel ? `select ${itemsLabel}` : 'any')}
                    </Badge>

                : <>
                    {target ?
                      target(items)
                    : <div className='flex gap-2'>
                        {selectedItems.map(i =>
                          item ?
                            item(i)
                          : <WrapIf key={i.value} if={i.desc != null} wrap={c => <Tooltip label={i.desc}>{c}</Tooltip>}>
                              <Badge {...badgeProps?.(i)} color={i.color}>
                                {i.label}
                                {i.count == null ? '' : ` - ${i.count}`}
                              </Badge>
                            </WrapIf>
                        )}
                      </div>
                    }
                    <TargetRight />
                  </>
                }
              </Button>
            </>
          }
        </div>
      </Combo.Target>
      <Combo.Dropdown className='max-h-[50vh] overflow-y-auto'>
        {showSearch && (
          <Combo.Search
            size='md'
            value={search}
            onChange={event => setSearch(event.currentTarget.value)}
            placeholder={searchPlaceholder ?? 'Search'}
          />
        )}
        <Combo.Options>
          {pipe(
            items.filter(i => i.visible),
            items =>
              items.length == 0 ?
                <div className='text-gray-300 font-bold'>no {itemsLabel ?? 'items'} yet..</div>
              : items.map(i => (
                  <Combo.Option
                    value={i.value}
                    key={i.value}
                    className={`cursor-pointer max-w-[40em] !py-2`}
                    onClick={e => e.stopPropagation()}>
                    {item ?
                      item(i)
                    : <>
                        <div className='flex gap-2 items-center'>
                          {i.selected && <CheckIcon size='1em' className='text-gray-400' />}
                          <Badge {...badgeProps?.(i)} color={i.color}>
                            {i.label}
                            {i.count == null ? '' : ` - ${i.count}`}
                          </Badge>
                        </div>
                        {i.desc && <div className='mt-1'>{i.desc}</div>}
                      </>
                    }
                  </Combo.Option>
                ))
          )}
        </Combo.Options>
      </Combo.Dropdown>
    </Combo>
  )
}

export type ComboMultiInputProps<T extends string, M extends ItemMd = ItemMd> = Omit<
  ComboMultiProps<T, M>,
  'children'
> &
  Partial<Pick<PillsInputProps, 'label' | 'size'>> & {placeholder?: string}

export const ComboMultiInput = <T extends string = string, M extends ItemMd = ItemMd>(
  props: ComboMultiInputProps<T, M>
) => {
  const {placeholder, label, size, showClear, item, ...comboProps} = props
  return (
    <ComboMulti
      {...comboProps}
      item={
        item ??
        (i => (
          <>
            <div className='flex gap-2 items-center'>
              {i.selected && <CheckIcon size='1em' className='text-gray-400 w-4' />}
              <div className='min-w-0 flex-1'>
                <span className='font-bold'>{i.label}</span>
                {i.count == null ?
                  <></>
                : <span>` - ${i.count}`</span>}
              </div>
            </div>
            {i.desc && <div className='mt-1'>{i.desc}</div>}
          </>
        ))
      }>
      {(values, combo) => {
        const selectedItems = values.filter(v => v.selected)
        return (
          <div className='flex items-center'>
            <PillsInput
              {...{placeholder, label, size}}
              className={comboProps.classNames?.input}
              pointer
              rightSection={<Combo.Chevron />}
              rightSectionPointerEvents='none'
              onClick={() => combo.openDropdown()}>
              <Pill.Group>
                {selectedItems.map(v => (
                  <Pill
                    className='pointer-events-none !max-w-[20em]'
                    classNames={{label: 'truncate'}}
                    styles={{label: {}}}
                    key={v.value}>
                    {v.label}
                  </Pill>
                ))}
                {selectedItems.length == 0 && <span className='text-gray-400'>{placeholder}</span>}
              </Pill.Group>
            </PillsInput>
            {showClear && !isEmpty(selectedItems) && (
              <CloseButton
                className='point m-1'
                size='sm'
                onClick={e => {
                  e.stopPropagation()
                  comboProps.onChange([], {mode: 'clear'})
                }}
              />
            )}
          </div>
        )
      }}
    </ComboMulti>
  )
}
